import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Paper, Stack, Group, Text, Button, Badge, Modal } from '@mantine/core'
import {
  IconPlayerPlay, IconRefresh, IconPlus, IconChartRadar, IconFlagCheck,
} from '@tabler/icons-react'
import { useEndTeamStory } from '../../hooks/useTeam'

/**
 * Active-story panel. Shows the current team-story and routes the player into
 * /play/:storyId?team=<teamId>&ts=<teamStoryId>. Team identity travels in the
 * URL (not a persisted atom), so the session is tagged on the backend and the
 * context can never go stale across the team's sequence of stories.
 *
 * Facilitator gets two independent controls: End Story (force-completes the
 * active story so its Team Analysis surfaces in Past Stories — used when some
 * members have abandoned) and Add Next Story (the existing bundled flow which
 * implicitly completes the prior story). When the last active member finishes,
 * the server auto-ends the story, so End Story is only needed for stragglers.
 *
 * View Team Analysis link appears for anyone (members + facilitator) as soon
 * as one player session has completed, even mid-story.
 */
export function TeamCurrentStoryPanel({
  team,
  currentStory,
  isFacilitator,
  isInactiveMember,
  canPlay,
  onAddNext,
}) {
  const navigate = useNavigate()
  const [confirmEndOpen, setConfirmEndOpen] = useState(false)
  const endStory = useEndTeamStory(team?.id)

  if (!currentStory) {
    return (
      <Paper p="lg" className="glass">
        <Stack gap="sm">
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" tt="uppercase" lts={1}>
            Current Story
          </Text>
          <Text size="sm" c="dimmed">No active story right now.</Text>
          {isFacilitator && (
            <Group>
              <Button size="sm" leftSection={<IconPlus size={14} />} onClick={onAddNext}>
                Add a story
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>
    )
  }

  const status = currentStory.my_session_status
  const completedSessionsCount = currentStory.completed_sessions_count || 0
  const launch = () => {
    const params = new URLSearchParams({
      team: String(team.id),
      ts: String(currentStory.team_story_id),
    })
    navigate(`/play/${currentStory.story_id}?${params.toString()}`)
  }

  const confirmEnd = () => {
    endStory.mutate(currentStory.team_story_id, {
      onSuccess: () => setConfirmEndOpen(false),
    })
  }

  return (
    <Paper p="lg" className="glass" style={{ borderColor: 'rgba(0, 229, 255, 0.2)' }}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" tt="uppercase" lts={1}>
              Current Story
            </Text>
            <Text size="lg" fw={600} c="cyan.3" ff="Orbitron, sans-serif" className="glow-text-cyan">
              {currentStory.story_name}
            </Text>
          </Stack>
          {status && (
            <Badge size="sm" color={status === 'completed' ? 'cyan' : 'gray'} variant="light">
              {status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In progress' : 'Not started'}
            </Badge>
          )}
        </Group>

        <Group gap="sm">
          {canPlay && status !== 'completed' && (
            <Button
              leftSection={status === 'in_progress' ? <IconRefresh size={16} /> : <IconPlayerPlay size={16} />}
              onClick={launch}
              className="glow-cyan"
            >
              {status === 'in_progress' ? 'Resume' : 'Play'}
            </Button>
          )}

          {status === 'completed' && currentStory.my_session_id && (
            <Button
              variant="light"
              color="cyan"
              leftSection={<IconChartRadar size={16} />}
              onClick={() => navigate(`/analysis/${currentStory.my_session_id}`)}
            >
              My Debrief
            </Button>
          )}

          {completedSessionsCount > 0 && (
            <Button
              variant="light"
              color="cyan"
              leftSection={<IconChartRadar size={16} />}
              onClick={() => navigate(`/teams/${team.id}/analysis/${currentStory.team_story_id}`)}
            >
              View Team Analysis
            </Button>
          )}

          {isInactiveMember && (
            <Text size="xs" c="dimmed">
              Your access was removed — you can no longer play this story.
            </Text>
          )}

          {isFacilitator && (
            <>
              <Button
                variant="light"
                color="orange"
                leftSection={<IconFlagCheck size={14} />}
                onClick={() => setConfirmEndOpen(true)}
              >
                End Story
              </Button>
              <Button
                variant="light"
                color="cyan"
                leftSection={<IconPlus size={14} />}
                onClick={onAddNext}
              >
                Add Next Story
              </Button>
            </>
          )}
        </Group>

        {canPlay && status !== 'completed' && (
          <Text size="xs" c="dimmed">
            You'll play this with your team — your responses roll up into Team Analysis.
          </Text>
        )}

        {isFacilitator && (
          <Text size="xs" c="dimmed">
            The team story auto-ends when the last active member finishes. Use
            <Text span fw={600}> End Story </Text>
            only if some members have abandoned and you want analysis now.
          </Text>
        )}
      </Stack>

      <Modal
        opened={confirmEndOpen}
        onClose={() => setConfirmEndOpen(false)}
        title="End this story?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This moves <Text span fw={600}>{currentStory.story_name}</Text> into
            Past Stories. Members still mid-play won't be able to finish their
            sessions for this team story. Team Analysis will be available
            immediately.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setConfirmEndOpen(false)}>
              Cancel
            </Button>
            <Button color="orange" onClick={confirmEnd} loading={endStory.isPending}>
              End Story
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}
